import { useCallback, useRef, useState } from "react";

import awaitVerificationVerdict from "../logic/awaitVerificationVerdict";
import mergeHookDelta from "../logic/mergeHookDelta";
import resolveStandardizedCommit from "../logic/resolveStandardizedCommit";
import { buildRevertedAddress } from "../logic/standardizationDiff";

const NOOP = () => {};

/**
 * Hold-the-typed-values save for any address surface that owns its Save button.
 *
 * While editing, the inputs keep exactly what the user typed (the consumer
 * passes `holdTypedValues: true` to `useAddressAutocomplete`); the autocomplete
 * autofill that fires during a Save is CAPTURED here rather than painted into
 * the buffer, so the standardized form never flashes mid-edit. handleSave POSTs
 * the canonical and stashes the undo payload; the **consumer** decides when to
 * reveal it (`revealStandardization`) — admin reveals once its editing slide
 * unmounts, a stay-mounted surface reveals on the POST's success.
 *
 * `merge` adapts the autofill delta to the surface's wire shape: admin/boards
 * keep the ISO `country` (default `mergeHookDelta`); the numeric-`countryID`
 * surfaces (Network, Transfers) pass `mergeCountryIDAddressDelta`.
 */
const useStandardizedSave = ({
  clientValue,
  setClientValue,
  onSave,
  // Surfaces that track their own dirty state (e.g. Network derives it) can omit
  // this; admin uses it to latch its edit session.
  markEdited = NOOP,
  merge = mergeHookDelta,
  // The key the street lives under in this surface's value shape. Admin/boards
  // are `addr1`-native (default); the numeric-`countryID` surfaces use `street`,
  // so the standardization diff/undo run against the right key and the base-
  // retain undo payload (always `addr1`-shaped from the engine) is mapped back.
  streetField = "addr1",
}) => {
  // True only for the duration of a Save commit. While set, autofill deltas
  // are captured (below) rather than written to the visible buffer.
  const savingRef = useRef(false);
  // Accumulates the autofill delta produced during a Save so handleSave can
  // POST the standardized values without ever flashing them into the inputs.
  const pendingSaveDeltaRef = useRef(null);

  // Hook delta arrives on autofill / metadata writes. The merge keeps the
  // surface's country shape (ISO `country` for admin, numeric `countryID` for
  // the countryID surfaces).
  const handleHookChange = useCallback(
    (delta) => {
      markEdited();
      // During a Save commit the autocomplete autofills the standardized
      // values. Writing them to `clientValue` here would repaint the still-
      // mounted inputs (the "west → W" flash through the save spinner before
      // the read-only transition). Capture the delta instead and let
      // handleSave apply it as the POST payload; the inputs keep the user's
      // typed text until they unmount.
      if (savingRef.current) {
        pendingSaveDeltaRef.current = {
          ...(pendingSaveDeltaRef.current || {}),
          ...delta,
        };
        return;
      }
      setClientValue(merge(clientValueRef.current, delta));
    },
    [markEdited, merge, setClientValue]
  );

  // Refs mirror the latest values so `handleSave`
  // can read the freshest data without re-binding on every render.
  // `onSaveRef` is especially load-bearing: the autocomplete autofill that
  // fires inside `handleSave` writes a new `clientValue` and produces a
  // new `onSave`, but the closure-captured `onSave` would POST the
  // pre-autofill values. Reading through the ref always gets the latest.
  const resolveOnGroupBlurRef = useRef(null);
  const fetchInFlightRef = useRef(null);
  // Set by the consumer to the hook's `commitVisibleGhosts`. handleSave fires it
  // first so a Save click freezes the visible ghost to black text exactly as the
  // keyboard accept (Enter) does — see handleSave.
  const commitVisibleGhostsRef = useRef(null);
  const onSaveRef = useRef(onSave);
  // Mirrors the latest controlled value so `handleSave` can read the typed
  // values before the save autofill and the standardized values after, off the
  // same ref (the closure-captured `clientValue` would be the pre-save copy).
  const clientValueRef = useRef(clientValue);
  onSaveRef.current = onSave;
  clientValueRef.current = clientValue;

  // The address as the user typed it, captured at the save that standardized
  // it — null when the last save made no change. Drives the read-only undo
  // notice and is the exact payload undo re-persists.
  const [standardization, setStandardization] = useState(null);
  // The standardization computed during a Save, held until the consumer
  // confirms the post-save view is on screen. We stash it here instead of
  // opening the notice mid-save so it can't surface before the saved address it
  // explains (see `revealStandardization`).
  const pendingStandardizationRef = useRef(null);
  // Promote the stashed standardization once the consumer's post-save view is
  // up — admin's read-only slide has unmounted the editor, or a stay-mounted
  // surface's POST has succeeded and repainted the canonical. Guarded on a
  // pending value so a cancel, or a save that standardized nothing, is a no-op
  // that never clears or surfaces a notice.
  const revealStandardization = useCallback(() => {
    if (pendingStandardizationRef.current == null) return;
    setStandardization(pendingStandardizationRef.current);
    pendingStandardizationRef.current = null;
  }, []);

  // Save handler: explicit commit. The click reads as acceptance of the
  // shadow-text candidate, so we pass `acceptTopCandidate=true` and let
  // the hook autofill from the top candidate even when there are multiple
  // results — same row the shadow was offering, no second Smarty roundtrip.
  // An address with no acceptable candidate settles unverified and still POSTs.
  //
  // The parent's `valueIsDifferent` gate already hides Save when the
  // buffer matches the persisted value, so we don't re-check here — the
  // local "addressIsDirty" snapshot would disagree with the parent after
  // a cancel-then-reopen with a carried-over buffer and silently swallow
  // the POST.
  const handleSave = useCallback(async () => {
    // Freeze the visible ghost (typed text + each field's gray suffix) to black
    // text first, exactly as the keyboard accept (Enter) does — a Save click is
    // an acceptance of the shadow the user is looking at, so it shouldn't leave
    // the gray ghost lingering while the save resolves. This runs BEFORE
    // `savingRef` flips, so it paints (it isn't captured); it writes the user's
    // own spelling (no metadata), so the standardized canonical the capture
    // below produces is unaffected — and `typed` still reads the pre-paint value
    // (the setClientValue hasn't flushed), matching Enter's POST exactly.
    commitVisibleGhostsRef.current?.();
    // `holdTypedValues` keeps the inputs as the user typed them right up to
    // here, so this snapshot is exactly what they entered.
    const typed = clientValueRef.current;
    // Capture the autofill instead of letting it write the buffer, so the
    // inputs never flash to the standardized form during the save spinner.
    savingRef.current = true;
    pendingSaveDeltaRef.current = null;
    let verdict;
    try {
      verdict = await awaitVerificationVerdict(
        resolveOnGroupBlurRef,
        fetchInFlightRef
      );
    } finally {
      savingRef.current = false;
    }
    const captured = pendingSaveDeltaRef.current;
    pendingSaveDeltaRef.current = null;

    // The shared commit decision — the same classifier the boards/transfers
    // `commitRef` runs (see `resolveStandardizedCommit`), so the deferred /
    // unit-slot / unverifiable / surprise-standardization branching can't drift
    // between the two hold-save paths. Side effects (paint, POST, stash) stay
    // here; the helper only classifies.
    const decision = resolveStandardizedCommit({
      typed,
      captured,
      verdict,
      merge,
      streetField,
    });

    // Defensive: `awaitVerificationVerdict` force-settles when its poll window
    // expires (it never returns "deferred" anymore — a slow/down Smarty settles
    // unverifiable and the typed address POSTs unverified via the branch below).
    // Kept as a guard: if a deferred verdict ever reached here we'd hold edit
    // mode open rather than POST an unresolved buffer.
    if (decision.kind === "deferred") {
      if (captured) setClientValue(merge(clientValueRef.current, captured));
      pendingStandardizationRef.current = null;
      return { success: false };
    }
    if (decision.kind === "noChange") {
      // No autofill (not dirty / nothing to verify): POST the typed buffer.
      pendingStandardizationRef.current = null;
      return onSaveRef.current();
    }
    // A multi-unit GROUP autofilled a "…Apt _" slot the user hasn't filled
    // (the sentinel is still in the standardized addr1). This surface keeps its
    // inputs mounted while editing, so — like every other surface — paint the
    // slot into the buffer (the hook's slot-select effect highlights the "_")
    // and HOLD the editor open instead of POSTing the sentinel. The user types
    // the unit (a real address) or, leaving it unfilled, the next Save strips it
    // and settles the address unverified. Never persist the "_".
    if (decision.kind === "unitSlot") {
      setClientValue(decision.standardized);
      pendingStandardizationRef.current = null;
      return { success: false };
    }
    // `commit`: POST the standardized value as an override — the visible buffer
    // was never touched.
    const { standardized, standardization: undoPayload, unverifiable } =
      decision;
    // The address settled unverifiable (did-you-mean / zero candidates / base +
    // appended unit). Mirror just the verdict flags into the buffer — not the
    // canonical subfields, so nothing flashes into the still-mounted inputs —
    // so the "couldn't verify" callout surfaces in the post-save view as soon
    // as it's on screen, without waiting on a backend refetch (§5).
    if (unverifiable) {
      setClientValue(
        merge(clientValueRef.current, {
          validationSkipped: "1",
          isVerified: false,
          verificationProvider: "",
          verifiedAt: "",
        })
      );
    }
    // Stash, don't show — revealStandardization opens it once the post-save view
    // is up. `undoPayload` is null unless the saved address differs from what
    // the user *saw* (the helper keys base-retain/bare-building on the street-only
    // decision and an ordinary standardization on `savedDiffersFromSeen`).
    pendingStandardizationRef.current = undoPayload;
    // On a surprise standardization, attach the member's typed original to the
    // saved/POST object (never to the visible-value buffer — the inputs are
    // already gone) so field history can later show what they typed. Omitted
    // when there's no surprise.
    return onSaveRef.current(
      undoPayload
        ? { ...standardized, standardizationOriginal: undoPayload }
        : standardized
    );
  }, [merge, setClientValue, streetField]);

  // Undo: re-persist exactly what the user typed, marked unverified/skipped
  // (the same off-state editing a normalized field produces). Pass the value
  // straight to onSave as an override so the POST doesn't wait on a re-render,
  // and mirror it into the buffer so the read-only view updates immediately.
  const handleUndo = useCallback(async () => {
    if (!standardization) return;
    const reverted = buildRevertedAddress(standardization);
    setStandardization(null);
    setClientValue(reverted);
    return onSaveRef.current(reverted);
  }, [standardization, setClientValue]);

  return {
    handleHookChange,
    handleSave,
    handleUndo,
    standardization,
    setStandardization,
    revealStandardization,
    resolveOnGroupBlurRef,
    fetchInFlightRef,
    commitVisibleGhostsRef,
  };
};

export default useStandardizedSave;
